// ============================================
// src/features/lockers/components/ui/LockerAssignmentInfo.tsx
// ============================================

import React from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import { Person, Email, Hotel, AccessTime, CheckCircle } from '@mui/icons-material';
import { Locker } from '../../types';

export interface LockerAssignmentInfoProps {
  record: Locker;
  showDetails?: boolean;
}

export const LockerAssignmentInfo: React.FC<LockerAssignmentInfoProps> = ({
  record,
  showDetails = true,
}) => {
  if (!record.assignment) {
    return (
      <Typography variant="caption" color="text.secondary">
        Disponible
      </Typography>
    );
  }

  const { assignment } = record;
  
  // Usar las propiedades que existen en AssignmentSummary
  // Ajusta estos nombres según las propiedades reales de tu tipo
  const displayText = (assignment as any).displayReference || 
                      assignment.guestName || 
                      assignment.roomNumber || 
                      'Asignado';
  
  const assignmentId = (assignment as any).assignmentId || assignment.id || '';
  const status = (assignment as any).status || 'Active';
  
  // Determinar si es un email
  const isEmail = displayText?.includes('@');
  const isActive = status === 'Active';
  
  // Calcular tiempo desde la asignación
  const assignedDate = new Date(assignment.assignedAt);
  const now = new Date();
  const daysSinceAssignment = Math.floor((now.getTime() - assignedDate.getTime()) / (1000 * 60 * 60 * 24));
  
  const getIcon = () => {
    if (isEmail) return <Email sx={{ fontSize: 16 }} />;
    if (assignment.roomNumber) return <Hotel sx={{ fontSize: 16 }} />;
    return <Person sx={{ fontSize: 16 }} />;
  };

  const getStatusColor = () => {
    if (!isActive) return 'default';
    if (daysSinceAssignment > 7) return 'warning';
    return 'success';
  };

  const getTimeText = () => {
    if (daysSinceAssignment === 0) return 'Hoy';
    if (daysSinceAssignment === 1) return 'Ayer';
    if (daysSinceAssignment < 7) return `Hace ${daysSinceAssignment} días`;
    if (daysSinceAssignment < 30) return `Hace ${Math.floor(daysSinceAssignment / 7)} semanas`;
    return `Hace ${Math.floor(daysSinceAssignment / 30)} meses`;
  };

  if (!showDetails) {
    return (
      <Chip
        icon={getIcon()}
        label={displayText}
        size="small"
        color={getStatusColor()}
        variant="outlined"
      />
    );
  }

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" display="block">
            <strong>Referencia:</strong> {displayText}
          </Typography>
          {assignmentId && (
            <Typography variant="caption" display="block">
              <strong>ID Asignación:</strong> {assignmentId}
            </Typography>
          )}
          {assignment.roomNumber && (
            <Typography variant="caption" display="block">
              <strong>Habitación:</strong> {assignment.roomNumber}
            </Typography>
          )}
          {assignment.guestName && (
            <Typography variant="caption" display="block">
              <strong>Huésped:</strong> {assignment.guestName}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            <strong>Estado:</strong> {status}
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Asignado:</strong> {assignedDate.toLocaleString('es-ES')}
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Tiempo asignado:</strong> {getTimeText()}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getIcon()}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px'
              }}
            >
              {displayText}
            </Typography>
            {isActive && (
              <CheckCircle 
                sx={{ 
                  fontSize: 14, 
                  color: daysSinceAssignment > 7 ? 'warning.main' : 'success.main' 
                }} 
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {getTimeText()} • {
                isEmail ? 'Usuario' : 
                assignment.roomNumber ? `Habitación ${assignment.roomNumber}` :
                assignment.type ? assignment.type : 'Asignado'
              }
            </Typography>
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};